import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import Avatar from './Avatar';
import { ApiError, tarotApi } from '../lib/api';
import { toApiLocale } from '../lib/locale';
import { PERSONAS, lockedPersona, lockedTheme, type Persona } from '../lib/personas';
import {
  VERSION_KEYS,
  VERSION_PROFILES,
  type VersionKey,
} from '../lib/versions';
import { START_COPY, resolveUiLanguage } from '../lib/publicCopy';

const VERSION_ACCENTS: Record<VersionKey, string> = {
  healing: 'linear-gradient(135deg, #f8e9de, #c77f75 58%, #7f3835)',
  ritual: 'radial-gradient(circle at 44% 34%, #d9bf72, #1c172c 52%, #08060d)',
  clarity: 'linear-gradient(135deg, #ffffff, #f4efe4 54%, #111111)',
};

const VERSION_VISUALS: Record<VersionKey, string> = {
  healing: '/image2/version-healing.svg',
  ritual: '/image2/version-ritual.svg',
  clarity: '/image2/version-clarity.svg',
};

function recommendPersonaFromConcern(text: string, fallback: Persona): Persona {
  const value = text.trim();
  if (!value) return fallback;

  const scores: Record<Persona, number> = { luna: 0, orion: 0, sage: 0 };
  const rules: Array<[Persona, RegExp]> = [
    ['luna', /感情|關係|伴侶|分手|復合|喜歡|愛|心累|迷惘|孤單|家人|朋友|love|relationship|partner|breakup|family|friend|relación|pareja|amor|familia|ami|amour|famille/],
    ['orion', /命運|靈魂|前世|夢|直覺|儀式|徵兆|回頭|放不下|注定|神秘|fate|destiny|soul|dream|intuition|sign|ritual|destino|sueño|intuición|ritual|âme|rêve|signe|mystique/],
    ['sage', /工作|職涯|事業|決定|選擇|分析|理智|計畫|方向|金錢|合作|創業|work|career|decision|choice|analysis|money|business|plan|trabajo|carrera|decisión|dinero|negocio|travail|carrière|choix|argent/],
  ];

  for (const [persona, pattern] of rules) {
    if (pattern.test(value)) scores[persona] += 2;
  }

  for (const char of value) {
    const bucket = char.charCodeAt(0) % 3;
    if (bucket === 0) scores.luna += 0.08;
    if (bucket === 1) scores.orion += 0.08;
    if (bucket === 2) scores.sage += 0.08;
  }

  return ([...PERSONAS] as Persona[]).sort((a, b) => scores[b] - scores[a])[0] ?? fallback;
}

interface StartReadingExperienceProps {
  initialVersion?: VersionKey;
  showBackLink?: boolean;
}

export default function StartReadingExperience({
  initialVersion = 'healing',
  showBackLink = false,
}: StartReadingExperienceProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const initialProfile = VERSION_PROFILES[initialVersion];
  const [selectedVersion, setSelectedVersion] = useState<VersionKey>(initialVersion);
  const [concern, setConcern] = useState(initialProfile.sampleConcern);
  const [concernDirty, setConcernDirty] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<Persona>(initialProfile.persona);
  const [autoRecommended, setAutoRecommended] = useState<Persona | null>(null);
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readerLock = lockedPersona();
  const themeLock = lockedTheme();
  const profile = VERSION_PROFILES[selectedVersion];
  const copy = START_COPY[resolveUiLanguage(i18n.resolvedLanguage ?? i18n.language)];
  const effectivePersona = readerLock ?? selectedPersona;
  const effectiveTheme = themeLock ?? profile.theme;
  const selectedPersonaCopy = copy.personas[effectivePersona];
  const currentSampleConcern = copy.versions[selectedVersion].sampleConcern;

  useEffect(() => {
    const next = VERSION_PROFILES[initialVersion];
    setSelectedVersion(initialVersion);
    setSelectedPersona(readerLock ?? next.persona);
    setAutoRecommended(null);
    setConcernDirty(false);
    document.documentElement.setAttribute('data-theme', themeLock ?? next.theme);
  }, [initialVersion, readerLock, themeLock]);

  useEffect(() => {
    if (!concernDirty) setConcern(currentSampleConcern);
  }, [concernDirty, currentSampleConcern]);

  function pickVersion(version: VersionKey) {
    const next = VERSION_PROFILES[version];
    setSelectedVersion(version);
    setSelectedPersona(readerLock ?? next.persona);
    setAutoRecommended(null);
    setError(null);
    document.documentElement.setAttribute('data-theme', themeLock ?? next.theme);
    if (!concernDirty) setConcern(copy.versions[version].sampleConcern);
  }

  function updateConcern(value: string) {
    setConcern(value);
    setConcernDirty(true);
  }

  function pickPersona(persona: Persona) {
    if (readerLock) return;
    setSelectedPersona(persona);
    setAutoRecommended(null);
  }

  function autoPickPersona() {
    if (readerLock) {
      setSelectedPersona(readerLock);
      setAutoRecommended(readerLock);
      return;
    }
    const recommended = recommendPersonaFromConcern(concern || currentSampleConcern, profile.persona);
    setSelectedPersona(recommended);
    setAutoRecommended(recommended);
  }

  async function start() {
    if (!consent || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      document.documentElement.setAttribute('data-theme', effectiveTheme);
      await tarotApi.mockLogin('demo@example.com');
      const session = await tarotApi.createSession({
        persona: effectivePersona,
        theme: effectiveTheme,
        locale: toApiLocale(i18n.resolvedLanguage),
        consent_version: 'v1',
      });
      navigate(`/session/${session.id}?concern=${encodeURIComponent(concern.trim() || currentSampleConcern)}`, {
        state: { initialConcern: concern.trim() || currentSampleConcern },
      });
    } catch (e) {
      setError(
        e instanceof ApiError
          ? copy.errors.api.replace('{{status}}', String(e.status))
          : copy.errors.network,
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="tarot-page-motion min-h-screen bg-[#f7f1e8] px-5 py-6 text-[#211b18] sm:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-6xl flex-col">
        {showBackLink && (
          <Link
            to="/"
            className="tarot-shape-spotlight-host w-fit rounded-full border border-[#d6c9be] bg-white/40 px-4 py-2 text-sm text-[#6f625a] backdrop-blur-md hover:text-[#211b18]"
          >
            ← {copy.backHome}
          </Link>
        )}

        <section className="grid flex-1 items-center gap-7 py-5 lg:grid-cols-[0.86fr_1.14fr] lg:py-8">
          <motion.div
            initial={{ y: 18, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.55 }}
            className="relative"
          >
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#9b3b36]">
              {copy.kicker}
            </p>
            <h1 className="mt-4 max-w-2xl font-display text-5xl leading-[0.96] text-[#120f0d] sm:text-6xl lg:text-7xl">
              {copy.title}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-[#5f534c]">
              {copy.subtitle}
            </p>

            <div className="mt-7 grid gap-3" aria-label={copy.versionSelectorAria}>
              {VERSION_KEYS.map((key) => {
                const item = copy.versions[key];
                const active = selectedVersion === key;
                return (
                  <motion.button
                    key={key}
                    type="button"
                    data-testid={`version-card-${key}`}
                    onClick={() => pickVersion(key)}
                    whileHover={{ y: -3, scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className={[
                      'tarot-liquid-glass tarot-shape-spotlight-host grid min-h-[104px] grid-cols-[92px_1fr] overflow-hidden border text-left shadow-[0_14px_42px_rgba(53,39,25,0.08)]',
                      active ? 'is-active border-[#9b3b36]' : 'border-[#dccdc1]',
                    ].join(' ')}
                    style={{ borderRadius: active ? '24px 24px 14px 24px' : '20px' }}
                  >
                    <span
                      className="relative block h-full overflow-hidden"
                      style={{ background: VERSION_ACCENTS[key] }}
                    >
                      <img
                        src={VERSION_VISUALS[key]}
                        alt=""
                        className="h-full w-full object-cover opacity-88"
                        draggable={false}
                      />
                    </span>
                    <span className="flex min-w-0 flex-col justify-center p-4">
                      <span className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-[#9b3b36]">
                        {active ? copy.versionState.active : copy.versionState.inactive}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="font-display text-2xl text-[#211b18]">{item.name}</span>
                        <span data-testid={`try-${key}`} className="rounded-full bg-[#111]/85 px-2.5 py-1 text-[0.62rem] font-semibold text-white">
                          {copy.versionState.badge}
                        </span>
                      </span>
                      <span className="mt-1 text-sm leading-relaxed text-[#6f625a]">
                        {item.promise}
                      </span>
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 18, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ duration: 0.58 }}
            className="tarot-liquid-glass tarot-glow-border relative overflow-hidden p-4 sm:p-6"
            style={{ borderRadius: '30px 30px 18px 30px' }}
          >
            <div
              className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full opacity-70 blur-3xl"
              style={{ background: VERSION_ACCENTS[selectedVersion] }}
            />
            <div className="relative grid gap-5 xl:grid-cols-[0.95fr_1.05fr]">
              <section className="tarot-shape-spotlight-host rounded-2xl border border-[#e0d4ca] bg-[#fffaf5]/68 p-4 backdrop-blur-md">
                <label className="block">
                  <span className="font-display text-3xl">{copy.concern.title}</span>
                  <textarea
                    data-testid="concern-input"
                    value={concern}
                    onChange={(e) => updateConcern(e.target.value)}
                    className="tarot-shape-spotlight-host mt-4 min-h-36 w-full resize-none rounded-2xl border border-[#d7c9be] bg-[#fffaf5]/82 p-4 text-lg leading-relaxed outline-none backdrop-blur-md focus:border-[#9b3b36] focus:shadow-[0_0_34px_rgba(155,59,54,0.16)]"
                    placeholder={copy.concern.placeholder}
                  />
                </label>
                <div className="mt-3 grid gap-2">
                  {copy.prompts.map((prompt) => {
                    const active = concern === prompt.text;
                    return (
                      <motion.button
                        key={prompt.id}
                        type="button"
                        onClick={() => {
                          setConcern(prompt.text);
                          setConcernDirty(true);
                        }}
                        whileHover={{ y: -2, scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        className={[
                          'tarot-shape-spotlight-host rounded-2xl border bg-white/52 p-3 text-left backdrop-blur-md',
                          active ? 'is-active border-[#9b3b36]' : 'border-[#e0d4ca] hover:border-[#bf8b7f]',
                        ].join(' ')}
                      >
                        <span className="flex items-center gap-2">
                          <span className="rounded border border-[#d9cabe] bg-white/70 px-1.5 py-0.5 font-mono text-[0.55rem] text-[#9b3b36]">
                            {prompt.tag}
                          </span>
                          <span className="font-display text-lg">{prompt.label}</span>
                        </span>
                        <span className="mt-1 block text-xs leading-relaxed text-[#6f625a]">
                          {prompt.text}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </section>

              <section className="tarot-shape-spotlight-host rounded-2xl border border-[#e0d4ca] bg-[#fffaf5]/68 p-4 backdrop-blur-md" aria-label={copy.reader.aria}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-[#9b3b36]">
                      {copy.reader.kicker}
                    </p>
                    <h2 className="font-display text-2xl">{copy.reader.title}</h2>
                  </div>
                  <Avatar persona={effectivePersona} className="h-14 w-14 shrink-0" alt={effectivePersona} />
                </div>
                <div className="grid gap-2">
                  {PERSONAS.map((persona) => {
                    const lockedOut = Boolean(readerLock && readerLock !== persona);
                    const picked = effectivePersona === persona && !autoRecommended;
                    return (
                      <motion.button
                        key={persona}
                        data-testid={`reader-${persona}`}
                        type="button"
                        onClick={() => pickPersona(persona)}
                        disabled={lockedOut}
                        whileHover={lockedOut ? undefined : { y: -2, scale: 1.01 }}
                        whileTap={lockedOut ? undefined : { scale: 0.985 }}
                        className={[
                          'tarot-shape-spotlight-host flex min-h-[82px] items-start gap-3 rounded-2xl border p-3 text-left transition',
                          lockedOut ? 'cursor-not-allowed opacity-38' : '',
                          picked
                            ? 'is-active border-[#9b3b36] bg-[#fff4ef]/82 shadow-[0_12px_36px_rgba(155,59,54,0.14)]'
                            : 'border-[#e0d4ca] bg-[#fffaf5] hover:border-[#bf8b7f]',
                        ].join(' ')}
                      >
                        <Avatar persona={persona} className="h-11 w-11 shrink-0" alt={persona} />
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2 font-display text-xl">
                            <span className="rounded border border-[#d9cabe] bg-white/70 px-1.5 py-0.5 font-mono text-[0.55rem] text-[#9b3b36]">
                              {copy.personas[persona].role}
                            </span>
                            {copy.personas[persona].name}
                          </span>
                          <span className="mt-1 block text-xs leading-relaxed text-[#6f625a]">
                            {copy.personas[persona].blurb}
                          </span>
                        </span>
                      </motion.button>
                    );
                  })}
                  <motion.button
                    data-testid="reader-auto"
                    type="button"
                    onClick={autoPickPersona}
                    whileHover={{ y: -2, scale: 1.01 }}
                    whileTap={{ scale: 0.985 }}
                    className={[
                      'tarot-shape-spotlight-host flex min-h-[82px] items-start gap-3 rounded-2xl border p-3 text-left transition',
                      autoRecommended
                        ? 'is-active border-[#111] bg-[#f3eee6]/84 shadow-[0_12px_36px_rgba(20,18,16,0.12)]'
                        : 'border-[#d3c8bd] bg-[#f5efe8] hover:border-[#111]',
                    ].join(' ')}
                  >
                    <img
                      src="/image2/readers/generated/avatar-auto.jpg"
                      alt=""
                      className="h-11 w-11 shrink-0 rounded-full object-cover"
                      draggable={false}
                    />
                    <span className="min-w-0">
                      <span className="block font-display text-xl">{copy.auto.title}</span>
                      <span data-testid="reader-auto-result" className="mt-1 block text-xs leading-relaxed text-[#6f625a]">
                        {autoRecommended
                          ? copy.auto.recommended.replace('{{name}}', copy.personas[autoRecommended].name)
                          : copy.auto.default}
                      </span>
                    </span>
                  </motion.button>
                  {(readerLock || themeLock) && (
                    <p data-testid="sku-lock-notice" className="rounded-xl border border-[#d3c8bd] bg-[#fffaf5]/72 px-3 py-2 font-mono text-[0.58rem] uppercase tracking-[0.16em] text-[#9b3b36]">
                      SKU lock · {readerLock ? copy.personas[readerLock].name : 'reader open'} · {themeLock ?? 'theme by version'}
                    </p>
                  )}
                </div>
              </section>
            </div>

            <div className="relative mt-5 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
              <label className="tarot-shape-spotlight-host flex cursor-pointer items-start gap-3 rounded-2xl border border-[#e0d4ca] bg-[#faf4ed]/72 p-4 text-sm leading-relaxed text-[#5f534c] backdrop-blur-md">
                <input
                  data-testid="consent-checkbox"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  {copy.consent}
                </span>
              </label>
              <button
                data-testid="start-reading"
                type="button"
                disabled={!consent || submitting}
                onClick={start}
                className="tarot-shape-spotlight-host rounded-full bg-[#111] px-7 py-4 text-sm font-semibold text-white shadow-[0_16px_46px_rgba(17,17,17,0.2)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitting ? copy.actions.submitting : copy.actions.startWith.replace('{{name}}', selectedPersonaCopy.name)}
              </button>
            </div>

            {error && (
              <p data-testid="start-error" className="relative mt-4 rounded-xl border border-[#9b3b36] bg-[#fff4f0] p-3 text-sm text-[#9b3b36]">
                {error}
              </p>
            )}
          </motion.div>
        </section>
      </div>

      {createPortal(
        <AnimatePresence>
          {submitting && <StartTransitionOverlay persona={effectivePersona} />}
        </AnimatePresence>,
        document.body,
      )}
    </main>
  );
}

function StartTransitionOverlay({ persona }: { persona: Persona }) {
  const { i18n } = useTranslation();
  const copy = START_COPY[resolveUiLanguage(i18n.resolvedLanguage ?? i18n.language)].transition;

  return (
    <motion.div
      className="fixed inset-0 z-[90] flex items-center justify-center overflow-hidden bg-[#f7f1e8]/62 backdrop-blur-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.22 } }}
    >
      <motion.div
        className="absolute inset-[12%] rounded-[42%] opacity-75 blur-3xl"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(184,122,111,0.34), transparent 70%)',
        }}
        initial={{ scale: 0.82 }}
        animate={{ scale: [0.82, 1.08, 0.96], opacity: [0.45, 0.92, 0.72] }}
        transition={{ duration: 1.1, ease: 'easeInOut' }}
      />
      <div className="tarot-ritual-ring" />
      <motion.div
        className="tarot-liquid-glass tarot-glow-border flex w-[min(90vw,420px)] flex-col items-center rounded-[28px] p-8 text-center"
        initial={{ y: 28, scale: 0.9, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: -20, scale: 0.94, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 160, damping: 18 }}
      >
        <Avatar persona={persona} className="h-20 w-20" alt={persona} />
        <p className="mt-5 font-mono text-[0.68rem] uppercase tracking-[0.22em] text-[#9b3b36]">
          {copy.kicker}
        </p>
        <h2 className="mt-2 font-display text-3xl text-[#211b18]">
          {copy.title}
        </h2>
        <div className="mt-6 flex gap-3">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="aspect-[3/5] w-12 rounded-lg border border-[#c9968c] bg-[#e4b8ad] shadow-[0_12px_28px_rgba(155,59,54,0.16)]"
              animate={{ y: [0, -12, 0], rotate: i === 0 ? [-5, -2, -5] : i === 2 ? [5, 2, 5] : [0, 0, 0] }}
              transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.12 }}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
